import { memo, ReactNode, useEffect, useState } from 'react';
import { getToken, useToken } from '@/modules/api';
import { bridgeAtom, chatIdAtom, isOpenChatAtom, isUnauthorizedAtom, useAtom, useValue } from '@/store';

function decodeJwt(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Invalid JWT or error decoding:', e);
    return null;
  }
}

export const TokenHandler = memo<{
  children?: ReactNode;
}>((props) => {
  const [token, setToken] = useToken();
  const [isTokenLoading, setIsTokenLoading] = useState(true);

  const [chatId, setChatId] = useAtom(chatIdAtom);
  const [isUnauthorized, setIsUnauthorized] = useAtom(isUnauthorizedAtom);
  const { apiKey, isAlwaysOpenWSConnection } = useValue(bridgeAtom);
  const isOpenChat = useValue(isOpenChatAtom);

  // TODO: revisit later, probably should be merged with "isUnauthorized" hook below
  //  keep only chatIdParam part
  useEffect(() => {
    const shouldGetToken = !isAlwaysOpenWSConnection && !token && !isOpenChat;
    const urlParams = new URLSearchParams(window.location.search);
    const chatIdParam = urlParams.get('chatId');

    if (!shouldGetToken && !chatIdParam) {
      // immediately disable a loading state if we don't need to fetch a token
      setIsTokenLoading(false);
    } else {
      // Two cases:
      //  1. Token is set, but we are in a chatIdParam mode (from a link). Need to fetch a new token
      //  2. Token is not set at all
      (async () => {
        const payload = chatIdParam
          ? {
              apiKey,
              chatId: parseInt(chatIdParam),
              userId: token ? decodeJwt(token)?.sub : null,
            }
          : {
              apiKey,
              token,
              customerId: window.loggedUserId,
              chatId,
            };
        const { id_token, chat_id } = await getToken(payload);
        setIsUnauthorized(false);
        setToken(id_token);
        if (chat_id) {
          setChatId(chat_id);
        }
        setIsTokenLoading(false);
      })();
    }
  }, [token, isAlwaysOpenWSConnection, setChatId]);

  // isUnauthorized is set only when a request returns 401 Unauthorized
  useEffect(() => {
    if (isUnauthorized) {
      (async () => {
        const { id_token, chat_id } = await getToken({ apiKey, token, customerId: window.loggedUserId, chatId });
        setIsUnauthorized(false);
        setToken(id_token);
        if (chat_id) {
          setChatId(chat_id);
        }
      })();
    }
  }, [apiKey, chatId, token, isUnauthorized, setIsUnauthorized, setToken, setChatId]);

  return isTokenLoading ? null : props.children;
});

TokenHandler.displayName = 'TokenHandler';
